import type { TierInfo } from "./PrivateApiSchemas";

export interface TieredPricingResult {
  tiers: TierInfo[];
  totalCost: number;
}

/**
 * Calculates tiered pricing based on usage and tier definitions
 */
export const calculateTieredPricing = (
  usage: number,
  tiers: Array<{ upTo: number | null; rate: number }>,
): TieredPricingResult => {
  const result: TieredPricingResult = {
    tiers: [],
    totalCost: 0,
  };

  // Sort tiers by 'upTo' value to ensure correct order
  const sortedTiers = [...tiers].sort((a, b) => {
    if (a.upTo === null) return 1;
    if (b.upTo === null) return -1;
    return a.upTo - b.upTo;
  });

  let remainingUsage = usage;
  let currentUsageStart = 0;

  for (const tier of sortedTiers) {
    const { upTo: tierUpTo, rate: tierRate } = tier;
    let tierUsage = 0;

    if (tierUpTo === null) {
      // Last tier - use all remaining usage
      tierUsage = remainingUsage;
    } else {
      // Calculate usage within this tier range
      const tierEnd = Math.min(tierUpTo, usage);
      const tierStart = Math.max(currentUsageStart, 0);
      tierUsage = Math.max(0, tierEnd - tierStart);
    }

    const tierCost = tierUsage * tierRate;

    result.tiers.push({
      upTo: tierUpTo,
      usage: tierUsage,
      rate: tierRate,
      cost: tierCost,
    });

    result.totalCost += tierCost;
    remainingUsage -= tierUsage;
    currentUsageStart = tierUpTo || usage;
  }

  return result;
};
